import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Container,
  Grid,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useDispatch, useSelector } from "../../store";
import {
  addJobEquipment,
  addJobHours,
  addJobMilestone,
  addJobNote,
  addJobVendor,
  completeJob,
  deleteJobEquipment,
  deleteJobHours,
  deleteJobVendor,
  editJob,
  getJobById,
  postJob,
  resetJob,
  setTemporaryEquipment,
  setTemporaryVendors,
  setHighlightJob,
  updateJob,
} from "../../slices/job";
import { resetSite, searchSite } from "../../slices/site";
import SearchParams, { defaultParams } from "../../types/searchParams";
import { getJobTypes, searchJobTypes } from "../../slices/jobType";
import { searchProblemCodes } from "../../slices/problemCode";
import JobHoursView from "../views/JobDashboard/JobHoursView";
import JobAttachmentView from "../views/JobDashboard/JobAttachmentView";
import JobNoteView from "../views/JobDashboard/JobNoteView";
import { getSimpleUsers } from "../../slices/user";
import AddHoursVM from "../../types/addHoursVM";
import AddNoteVM from "../../types/addNoteVM";
import { searchCategories } from "../../slices/category";
import {
  deleteAttachment,
  editAttachment,
  getAttachmentId,
  getJobAttachments,
  resetAttachment,
  setAddedAttachments,
  setTempAttachments,
} from "../../slices/attachment";
import { Base } from "../base";
import {
  ArrowBack,
  AttachFile,
  Info,
  MoreTime,
  NoteAdd,
} from "@mui/icons-material";
import TabNavButton from "../../types/tabNavButton";
import JobInformationTab from "../views/JobDashboard/Tabs/JobInformationTab";
import { initialJob } from "../../types/job";
import { searchStates } from "../../slices/options";
import { searchSiteManager } from "../../slices/siteManager";
import { searchSiteType } from "../../slices/siteType";
import { searchEquipment } from "../../slices/equipment";
import Attachment from "../../types/attachment";
import { handleToastState } from "../../slices/shared";
import Answer from "../../types/answerVM";
import { AnswerTypes } from "../../types/questionVM";
import JobAttachmentCopy from "../views/JobDashboard/SpecificViews/JobAttachmentCopy";
import { getHighlights } from "../../slices/calendarHighlight";
import { getAttachmentTypes } from "../../slices/attachmentType";

interface Props {}

const JobPage: React.FC<Props> = (props) => {
  const theme = useTheme();

  const navigate = useNavigate();
  const { id } = useParams();

  const dispatch = useDispatch();
  const {
    job,
    categories,
    temporaryEquipment,
    temporaryVendors,
    temporaryMilestones,
    status,
    getStatus,
    highlightJob,
  } = useSelector((store) => store.job);
  const { currentViewJobType } = useSelector((store) => store.jobType);
  const {
    jobAttachments,
    tempAttachments,
    status: attachmentStatus,
  } = useSelector((store) => store.attachments);
  const [completeModal, setCompleteModal] = useState<boolean>(false);
  const [deleteNoteModal, setDeleteNoteModal] = useState<{
    id?: number;
    note?: string;
    type?: "vendor" | "equipment" | "time";
  }>({});
  const [deleteHourModal, setDeleteHourModal] = useState<{
    id: number;
    note?: string;
  }>({ id: 0 });
  const [activeTab, setActiveTab] = useState(0);
  const [newUpload, setNewUpload] = useState<Attachment[]>([]);
  const [fileLength, setFileLength] = useState<number>(0);
  const [fileTypeModal, setFileTypeModal] = useState<boolean>(false);
  const xs = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (currentViewJobType) {
      if (+(id ?? "") > 0) {
        dispatch(getJobById(+id!));
        dispatch(getJobAttachments(+id!));
        // dispatch(getJobChildren(+id!));
      } else {
        dispatch(
          updateJob({
            ...initialJob,
            jobType: {
              id: currentViewJobType?.id,
              value: currentViewJobType?.name,
            },
          }),
        );
      }
      dispatch(resetSite());
      dispatch(setAddedAttachments([]));
      dispatch(searchSiteManager(defaultParams));
      dispatch(searchSiteType(defaultParams));
      dispatch(searchStates(defaultParams));
      dispatch(getJobTypes());
      dispatch(getAttachmentTypes());
    } else navigate("/home");
    dispatch(getSimpleUsers(true));
    dispatch(getHighlights());
    return () => {
      dispatch(setHighlightJob(false));
    };
  }, [dispatch, id]);

  const handleApiSearch = (
    api: "site" | "jobType" | "category" | "problemCode" | "equipment",
    search: SearchParams,
    filter?: "jobType" | "category",
  ) => {
    let filters: string[] = [];
    if (filter === "jobType") {
      filters.push(`jobtype:${currentViewJobType?.id}`);
    }
    if (filter === "category") {
      categories.forEach((x) => {
        filters.push(`category:${x.id ?? ""}`);
      });
    }

    let searchParams = { ...search, filters: filters };
    switch (api) {
      case "site":
        return dispatch(searchSite(searchParams));
      case "jobType":
        return dispatch(searchJobTypes(searchParams));
      case "category":
        return dispatch(searchCategories(searchParams));
      case "problemCode":
        return dispatch(searchProblemCodes(searchParams));
      case "equipment":
        return dispatch(searchEquipment(searchParams));
    }
  };

  const handleUploadAttachment = (files: File[]) => {
    setFileLength(files.length);
    setNewUpload(
      files.map((f) => {
        return {
          name: f.name,
          upload: f,
          jobId: job.id,
          attachmentType: undefined,
          note: "",
        };
      }),
    );
  };

  useEffect(() => {
    if (fileLength > 0 && fileLength === newUpload.length && !fileTypeModal) {
      setFileTypeModal(true);
      dispatch(getJobAttachments(job.id));
    }
  }, [dispatch, fileTypeModal, fileLength, job.id, newUpload]);

  const handleDeleteAttachment = (attachmentId: number) => {
    dispatch(deleteAttachment(attachmentId)).then((result) => {
      if (result.meta.requestStatus === "fulfilled") {
        if (job.id > 0) {
          dispatch(getJobAttachments(+id!));
          dispatch(resetAttachment());
        } else {
          const filteredTempAttachments = tempAttachments.filter(
            (a) => a.id !== attachmentId,
          );
          dispatch(setTempAttachments(filteredTempAttachments));
        }
      }
    });
  };

  const handleReset = () => {
    dispatch(resetJob());
    dispatch(setTempAttachments([]));
  };

  const handleCompleteJob = (values: any) => {
    dispatch(
      editJob({
        ...values,
        answers: values.answers.map((a: Answer) => {
          if (a.question?.answerType.id === AnswerTypes.WeeklyTask) {
            return {
              ...a,
              value:
                a.value && !isNaN(new Date(a.value as string).getTime())
                  ? new Date(a.value as string)
                  : "",
            };
          }
          if (typeof a.value === "object") {
            return { ...a, value: JSON.stringify(a.value) };
          } else return a;
        }),
        site: !!job?.site?.id ? job.site : undefined,
      }),
    ).then((result) => {
      if (result.meta.requestStatus === "fulfilled") {
        dispatch(completeJob(job.id)).then((result: any) => {
          if (result.meta.requestStatus === "fulfilled") {
            handleReset();
            navigate(
              currentViewJobType ? `/home/${currentViewJobType.id}` : "/home",
            );
          }
        });
        setCompleteModal(false);
      }
    });
  };

  const addEditJob = (values: any) => {
    if (values.id > 0) {
      dispatch(
        editJob({
          ...values,
          answers: values.answers.map((a: Answer) => {
            if (a.question?.answerType.id === AnswerTypes.WeeklyTask) {
              return {
                ...a,
                value:
                  a.value && !isNaN(new Date(a.value as string).getTime())
                    ? new Date(a.value as string)
                    : "",
              };
            }
            if (typeof a.value === "object") {
              return {
                ...a,
                value: a.value !== null ? JSON.stringify(a.value) : "",
              };
            } else if (typeof a.value === "undefined") {
              return { ...a, value: "" };
            } else return a;
          }),
          site: !!job?.site?.id ? job.site : undefined,
        }),
      ).then((result) => {
        if (result.meta.requestStatus === "fulfilled") {
          navigate(`/job/view/${values.id}`);
        }
      });
    } else {
      dispatch(
        postJob({
          ...values,
          answers: values.answers.map((a: Answer) => {
            if (a.question?.answerType.id === AnswerTypes.WeeklyTask) {
              return {
                ...a,
                value:
                  a.value && !isNaN(new Date(a.value as string).getTime())
                    ? new Date(a.value as string)
                    : "",
              };
            }
            if (typeof a.value === "object") {
              return {
                ...a,
                value: a.value !== null ? JSON.stringify(a.value) : "",
              };
            } else if (typeof a.value === "undefined") {
              return { ...a, value: "" };
            } else {
              return a;
            }
          }),
          jobType: currentViewJobType,
          highlightDate: highlightJob ? job.highlightDate : undefined,
        }),
      ).then((result: any) => {
        if (result.meta.requestStatus === "fulfilled") {
          navigate(`/job/view/${result.payload.id}`);
          if (temporaryEquipment.length > 0) {
            temporaryEquipment.forEach((equipment) => {
              dispatch(
                addJobEquipment({
                  equipmentId: equipment.equipment?.id,
                  quantity: equipment.quantity,
                  jobId: result.payload.id,
                }),
              );
            });
          }
          if (temporaryVendors.length > 0) {
            temporaryVendors.forEach((vendor) => {
              dispatch(
                addJobVendor({
                  vendor: vendor.vendor,
                  jobId: result.payload.id,
                }),
              );
            });
          }
          if (temporaryMilestones.length > 0) {
            temporaryMilestones.forEach((milestone) => {
              dispatch(
                addJobMilestone({
                  ...milestone,
                  jobId: result.payload.id,
                }),
              );
            });
          }
          if (tempAttachments.length > 0) {
            tempAttachments.forEach((file) => {
              dispatch(
                editAttachment({
                  ...file,
                  jobId: result.payload.id,
                }),
              );
            });
          }
        }
      });
      dispatch(setHighlightJob(false));
    }
    handleReset();
  };

  const handleRemoveNote = () => {
    switch (deleteNoteModal.type) {
      case "equipment":
        if (job.id === 0) {
          const filteredEquipment = temporaryEquipment.filter(
            (te) => te.id !== deleteNoteModal.id,
          );
          dispatch(setTemporaryEquipment(filteredEquipment));
        } else {
          dispatch(deleteJobEquipment(deleteNoteModal?.id!));
        }
        break;
      case "vendor":
        if (job.id === 0) {
          const filteredVendors = temporaryVendors.filter(
            (tv) => tv.id !== deleteNoteModal?.id,
          );
          dispatch(setTemporaryVendors(filteredVendors));
        } else {
          dispatch(deleteJobVendor(deleteNoteModal?.id!));
        }
        break;
      default:
        break;
    }
    setDeleteNoteModal({ id: undefined, note: undefined, type: undefined });
  };

  const handleDeleteHours = () => {
    dispatch(deleteJobHours(deleteHourModal.id));
    setDeleteHourModal({ id: 0 });
  };

  const handleDownload = (id: number) => {
    dispatch(getAttachmentId(id)).then((result) => {
      let file = result.payload as Attachment;
      let a = document.createElement("a");
      a.href = file?.url!;
      a.download = file.name!;
      a.click();
      a.remove();
    });
  };

  const handleGetAttachment = (id: number) => {
    dispatch(getAttachmentId(id));
  };

  const handleEditAttachment = (v: Attachment) =>
    dispatch(editAttachment(v)).then(({ meta }) => {
      if (meta.requestStatus === "fulfilled") {
        dispatch(resetAttachment());
        dispatch(getJobAttachments(+id!));
      }
    });

  const tabs: TabNavButton[] = [
    {
      id: 0,
      label: xs ? "Info" : "Information",
      element: (
        <JobInformationTab
          handleReset={handleReset}
          addEditJob={addEditJob}
          setCompleteModal={setCompleteModal}
          handleCompleteJob={handleCompleteJob}
          completeModal={completeModal}
        />
      ),
      icon: <Info />,
    },
    //Not currently tracking any job associations, if we add back in the future, will display again
    // ...(currentViewJobType?.abbrev === "P"
    //   ? [
    //       {
    //         id: 5,
    //         label: "Related Jobs",
    //         element: (
    //           <Grid
    //             container
    //             direction={"row"}
    //             gap={3}
    //             sx={{
    //               backgroundColor: theme.palette.background.paper,
    //               padding: { md: 5, xs: 2 },
    //               borderRadius: "10px",
    //               mb: 2,
    //             }}
    //           >
    //             <Grid size={{ xs: 12 }}>
    //               <JobViewChildren
    //                 title="Service Tickets"
    //                 jobs={jobChildren.filter((j) => j.jobTypeId === 1)}
    //               />
    //             </Grid>
    //             <Grid size={{ xs: 12 }}>
    //               <JobViewChildren
    //                 title="Small Jobs"
    //                 jobs={jobChildren.filter((j) => j.jobTypeId === 3)}
    //               />
    //             </Grid>
    //             <Grid size={{ xs: 12 }}>
    //               <JobViewChildren
    //                 title="Meetings"
    //                 jobs={jobChildren.filter((j) => j.jobTypeId === 7)}
    //               />
    //             </Grid>
    //           </Grid>
    //         ),
    //         icon: (
    //           <FontAwesomeIcon icon={getJobTypeIcon("faTicket")} size="lg" />
    //         ),
    //       },
    //     ]
    //   : []),
    ...(currentViewJobType?.logHours
      ? [
          {
            id: 1,
            label: "Hours",
            element: (
              <Grid
                container
                direction={"row"}
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  padding: { md: 5, xs: 2 },
                  borderRadius: "10px",
                  mb: 2,
                }}
              >
                <Grid size={{ xs: 12 }}>
                  <JobHoursView
                    apiGetAllUsers={() => dispatch(getSimpleUsers(false))}
                    apiCategorySearch={(search: SearchParams) =>
                      handleApiSearch("category", search, "jobType")
                    }
                    apiAddHours={(hours: AddHoursVM) => {
                      dispatch(addJobHours(hours));
                    }}
                    setDeleteHourModal={setDeleteHourModal}
                  />
                </Grid>
              </Grid>
            ),
            icon: <MoreTime />,
          },
        ]
      : []),
    {
      id: 2,
      label: "Notes",
      element: (
        <Grid
          container
          direction={"row"}
          sx={{
            backgroundColor: theme.palette.background.paper,
            padding: { md: 5, xs: 2 },
            borderRadius: "10px",
            mb: 2,
          }}
        >
          <Grid size={{ xs: 12 }}>
            <JobNoteView
              apiAddNote={(note: AddNoteVM) => {
                dispatch(addJobNote(note));
              }}
            />
          </Grid>
        </Grid>
      ),
      icon: <NoteAdd />,
    },
    {
      id: 3,
      label: "Attachments",
      element: (
        <Grid
          container
          direction={"row"}
          sx={{
            backgroundColor: theme.palette.background.paper,
            padding: { md: 5, xs: 2 },
            borderRadius: "10px",
            mb: 2,
          }}
        >
          <Grid size={{ xs: 12 }}>
            <JobAttachmentView
              uploadAttachments={handleUploadAttachment}
              title={"Attachments"}
              apiDeleteAttachment={handleDeleteAttachment}
              attachments={job.id > 0 ? jobAttachments : tempAttachments}
              disabled={job.id === 0 || attachmentStatus === "loading"}
              handleDownload={handleDownload}
              getAttachment={handleGetAttachment}
              handleEditAttachment={handleEditAttachment}
            />
          </Grid>
        </Grid>
      ),
      icon: <AttachFile />,
    },
  ];

  const pageTitle =
    job.id === 0 ? `New ${currentViewJobType?.name}` : `Edit ${job.title}`;

  return (
    <>
      <Base.TitleBar title={pageTitle} />
      {getStatus === "loading" && !job ? (
        <Box sx={{ marginX: 3, marginTop: 1 }}>
          <Base.Text text="Loading, please wait..." variant="h3" />
          <CircularProgress />
        </Box>
      ) : (
        <Container maxWidth="xl">
          <Grid
            container
            justifyContent={"space-between"}
            alignItems={"center"}
            sx={{
              display: { xs: "none", sm: "flex" },
              my: 2,
            }}
          >
            <Grid>
              <Base.IconButton
                icon={<ArrowBack />}
                label="Back"
                onClick={() => {
                  if (job.id > 0) {
                    navigate(`/job/view/${job.id}`);
                  } else {
                    navigate(-1);
                  }
                }}
              />
            </Grid>
            <Grid>
              <Base.Text text={pageTitle} variant="h3" />
            </Grid>
            <Grid />
          </Grid>
          <Base.TabNav
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            tabs={tabs}
            disabled={job.id === 0}
            disabledIndex={
              job.id === 0
                ? Array.from({ length: tabs.length }, (_, i) => i).filter((i) => i !== 0)
                : []
            }
            disabledMessage={(step) => {
              dispatch(
                handleToastState({
                  open: "error",
                  message: `This ${currentViewJobType?.name} must be saved first`,
                }),
              );
            }}
          />
          <Base.Modal
            open={fileTypeModal}
            onClose={() => {
              setFileTypeModal(false);
              setNewUpload([]);
              setFileLength(0);
            }}
          >
            <JobAttachmentCopy
              id={+id!}
              newUpload={newUpload}
              setNewUpload={setNewUpload}
              setFileTypeModal={setFileTypeModal}
              setFileLength={setFileLength}
            />
          </Base.Modal>
          <Base.Dialog
            open={deleteNoteModal.id !== undefined}
            handleCancel={() =>
              setDeleteNoteModal({
                id: undefined,
                note: undefined,
                type: undefined,
              })
            }
            confirmText="Delete"
            confirmButtonType="delete"
            title="Are you sure you would like delete this note? This cannot be undone."
            handleConfirm={handleRemoveNote}
            loading={status === "loading"}
          />
          <Base.Dialog
            open={deleteHourModal.id > 0}
            handleCancel={() => setDeleteHourModal({ id: 0 })}
            confirmText="Delete"
            confirmButtonType="delete"
            title="Are you sure you would like delete these hours? This cannot be undone."
            handleConfirm={handleDeleteHours}
            loading={status === "loading"}
          />
        </Container>
      )}
    </>
  );
};

export default JobPage;
